import React, { useMemo } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { DollarSign, FileText, AlertCircle, CheckCircle, Users, TrendingUp } from 'lucide-react';
export const BillingDashboard: React.FC = () => {
  const {
    invoices,
    walkInClients,
    patients,
    quotations,
    payments
  } = useData();
  const stats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const currentMonth = today.slice(0, 7);
    const todayPayments = payments.filter(p => p.date.startsWith(today));
    const monthPayments = payments.filter(p => p.date.startsWith(currentMonth));
    const todayRevenue = todayPayments.reduce((sum, p) => sum + p.amount, 0);
    const monthRevenue = monthPayments.reduce((sum, p) => sum + p.amount, 0);
    const outstandingInvoices = invoices.filter(i => i.status === 'Unpaid' || i.status === 'Partially Paid' || i.status === 'Overdue');
    const totalOutstanding = outstandingInvoices.reduce((sum, i) => sum + i.balanceDue, 0);
    const paidInvoices = invoices.filter(i => i.status === 'Paid');
    const overdueInvoices = invoices.filter(i => i.status === 'Overdue');
    return {
      todayRevenue,
      monthRevenue,
      totalOutstanding,
      outstandingCount: outstandingInvoices.length,
      paidCount: paidInvoices.length,
      overdueCount: overdueInvoices.length,
      quotationsCount: quotations.length,
      totalCustomers: patients.length + walkInClients.length,
      walkInCount: walkInClients.length,
      patientCount: patients.length
    };
  }, [invoices, walkInClients, patients, quotations, payments]);
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZM', {
      style: 'currency',
      currency: 'ZMW'
    }).format(amount);
  };
  return <div className="space-y-6" data-id="element-883">
      <div className="flex justify-between items-center" data-id="element-884">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-885">Billing Dashboard</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-886">
        <Card className="bg-blue-50 border-blue-200" data-id="element-887">
          <div className="flex items-center" data-id="element-888">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4" data-id="element-889">
              <DollarSign className="h-6 w-6" data-id="element-890" />
            </div>
            <div data-id="element-891">
              <p className="text-sm font-medium text-blue-600" data-id="element-892">
                Today's Revenue
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-893">
                {formatCurrency(stats.todayRevenue)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="bg-green-50 border-green-200" data-id="element-894">
          <div className="flex items-center" data-id="element-895">
            <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4" data-id="element-896">
              <TrendingUp className="h-6 w-6" data-id="element-897" />
            </div>
            <div data-id="element-898">
              <p className="text-sm font-medium text-green-600" data-id="element-899">
                Monthly Revenue
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-900">
                {formatCurrency(stats.monthRevenue)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="bg-amber-50 border-amber-200" data-id="element-901">
          <div className="flex items-center" data-id="element-902">
            <div className="p-3 rounded-full bg-amber-100 text-amber-600 mr-4" data-id="element-903">
              <AlertCircle className="h-6 w-6" data-id="element-904" />
            </div>
            <div data-id="element-905">
              <p className="text-sm font-medium text-amber-600" data-id="element-906">
                Outstanding Balance
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-907">
                {formatCurrency(stats.totalOutstanding)}
              </p>
              <p className="text-xs text-amber-700 mt-1" data-id="element-908">
                {stats.outstandingCount} invoices
              </p>
            </div>
          </div>
        </Card>

        <Card className="bg-purple-50 border-purple-200" data-id="element-909">
          <div className="flex items-center" data-id="element-910">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-4" data-id="element-911">
              <Users className="h-6 w-6" data-id="element-912" />
            </div>
            <div data-id="element-913">
              <p className="text-sm font-medium text-purple-600" data-id="element-914">
                Total Customers
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-915">
                {stats.totalCustomers}
              </p>
              <p className="text-xs text-purple-700 mt-1" data-id="element-916">
                {stats.patientCount} Registered • {stats.walkInCount} Walk-in
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-917">
        <Card title="Invoice Summary" data-id="element-918">
          <div className="space-y-4" data-id="element-919">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg" data-id="element-920">
              <div className="flex items-center" data-id="element-921">
                <CheckCircle className="h-5 w-5 text-green-500 mr-3" data-id="element-922" />
                <span className="font-medium text-gray-700" data-id="element-923">Paid Invoices</span>
              </div>
              <span className="font-bold text-gray-900" data-id="element-924">{stats.paidCount}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg" data-id="element-925">
              <div className="flex items-center" data-id="element-926">
                <AlertCircle className="h-5 w-5 text-amber-500 mr-3" data-id="element-927" />
                <span className="font-medium text-gray-700" data-id="element-928">
                  Unpaid / Partial
                </span>
              </div>
              <span className="font-bold text-gray-900" data-id="element-929">
                {stats.outstandingCount}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg" data-id="element-930">
              <div className="flex items-center" data-id="element-931">
                <AlertCircle className="h-5 w-5 text-red-500 mr-3" data-id="element-932" />
                <span className="font-medium text-gray-700" data-id="element-933">
                  Overdue Invoices
                </span>
              </div>
              <span className="font-bold text-gray-900" data-id="element-934">
                {stats.overdueCount}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg" data-id="element-935">
              <div className="flex items-center" data-id="element-936">
                <FileText className="h-5 w-5 text-blue-500 mr-3" data-id="element-937" />
                <span className="font-medium text-gray-700" data-id="element-938">Quotations</span>
              </div>
              <span className="font-bold text-gray-900" data-id="element-939">
                {stats.quotationsCount}
              </span>
            </div>
          </div>
        </Card>

        <Card title="Recent Transactions" data-id="element-940">
          {payments.length > 0 ? <div className="space-y-3" data-id="element-941">
              {payments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5).map(payment => <div key={payment.id} className="flex justify-between items-center p-3 border-b border-gray-100 last:border-0" data-id="element-942">
                    <div data-id="element-943">
                      <p className="font-medium text-gray-900" data-id="element-944">
                        {payment.receiptNumber}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-945">
                        {new Date(payment.createdAt).toLocaleString()} •{' '}
                        {payment.method}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-946">
                      <p className="font-bold text-green-600" data-id="element-947">
                        +{formatCurrency(payment.amount)}
                      </p>
                    </div>
                  </div>)}
            </div> : <div className="text-center py-8 text-gray-500" data-id="element-948">
              No recent transactions
            </div>}
        </Card>
      </div>
    </div>;
};